import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from '@mui/icons-material';
import LoopIcon from '@mui/icons-material/Loop';
import {
  Alert,
  Button,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { compact, concat, debounce, isEmpty, sortBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FullGcfComponentSection } from 'src/components/gcfComponentSection';
import { InputTextLabel } from 'src/components/input/Input.styled';
import {
  ComponentListModal,
  PreviewModal,
  SimpleDialog,
} from 'src/components/modal';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import {
  DEFAULT_ASSEMBLY_COMPONENT_CONFIG,
  MAX_ALLOWED_COMPONENTS,
} from 'src/constants';
import { CodJourney } from 'src/constants/assemblyJourneys';
import { useModal } from 'src/hooks';
import { useCodEvents } from 'src/hooks/useCodEvents';
import {
  ComponentTypes,
  DynamicSectionEvents,
  IAssemblyComponent,
  IComponent,
} from 'src/interfaces';
import { lang } from 'src/utils';
import {
  FileTypes,
  getBackgroundVideoDuration,
  getFileExtensionType,
} from 'src/utils/file-types';
import { getSectionPreviewData } from 'src/utils/gcf-helper';

import { BackupComponentList } from './BackupComponent/BackupComponentList';
import {
  ComponentInfoProps,
  DurationUpdateTypes,
} from './ComponentInfo.interface';
import {
  AddBackupButton,
  GridContainer,
  InputTextField,
  PreviewGrid,
  Title,
} from './ComponentInfo.styled';

export const ComponentInfo = (props: ComponentInfoProps) => {
  const [selectedPreviewComponent, setSelectedPreviewComponent] = useState<
    IAssemblyComponent | undefined
  >();

  const [openComponentListModal, setOpenComponentListModal] =
    React.useState(false);
  const removeComponentModal = useModal();
  const componentPreviewModal = useModal();
  const currentDate = dayjs().add(1, 'day').format('YYYY-MM-DD');

  useCodEvents(props.customerState, props.activeAssemblyJourney as CodJourney);
  const handleBackupComponentSelection = (
    selectedBackupComponent: IAssemblyComponent,
  ) => {
    setSelectedPreviewComponent(selectedBackupComponent);
    componentPreviewModal.onOpenModal();
  };

  const backupAssemblyComponent: IAssemblyComponent[] = useMemo(() => {
    if (props.backupAssemblyComponent || props.parentBackupAssemblyComponents) {
      let filteredBackupData = !props.editable
        ? props.backupAssemblyComponent &&
          props.backupAssemblyComponent.filter(
            (item) =>
              item.rotation_order === props.assemblyComponent.rotation_order,
          )
        : props.parentBackupAssemblyComponents?.filter(
            (item) =>
              item.rotation_order === props.assemblyComponent.rotation_order,
          );

      if (
        props.availableRotations > 1 &&
        props.assemblyComponent?.config?.duration
      ) {
        filteredBackupData = filteredBackupData.map(
          (backupAssmelbyComponent) => {
            const updatedBacupAssemblyComponent = {
              ...backupAssmelbyComponent,
            };
            // runs only if no config is defined
            if (!updatedBacupAssemblyComponent.config) {
              updatedBacupAssemblyComponent.config = {
                duration: 3000, // Initialize with a default value 3 second fade if config not present
                transition: 'fade',
              };
            }

            const componentBackgroundType = getFileExtensionType(
              backupAssmelbyComponent?.component?.config?.background,
            );

            // Only update if duration is different
            if (
              updatedBacupAssemblyComponent.config.duration !==
                props.assemblyComponent?.config?.duration &&
              componentBackgroundType !== FileTypes.VIDEO
            ) {
              updatedBacupAssemblyComponent.config.duration =
                props.assemblyComponent?.config?.duration ?? 0;
            }

            return updatedBacupAssemblyComponent;
          },
        );
      }

      return filteredBackupData.sort(
        (a, b) => (a.backup_order || 0) - (b.backup_order || 0),
      );
    }

    return [];
  }, [
    props.backupAssemblyComponent,
    props.assemblyComponent.rotation_order,
    props.parentBackupAssemblyComponents,
    props.editable,
    props.availableRotations,
    props.assemblyComponent?.config?.duration,
  ]);

  const handleRemoveComponent = () => {
    props.onDeletedAssemblyComponent();
    removeComponentModal.onCloseModal();
  };

  const [duration, setDuration] = React.useState(3);

  useEffect(() => {
    if (props.assemblyComponent?.config?.duration) {
      setDuration(props.assemblyComponent.config.duration / 1000);
    }
  }, [props.assemblyComponent?.config?.duration]);

  const handleChangeDuration = (
    value: unknown,
    type: DurationUpdateTypes = DurationUpdateTypes.MAIN,
  ) => {
    const numValue = value as number;
    const regex = /^[0-9\b]+$/;
    if (regex.test(numValue.toString())) {
      const parsedNumValue = parseInt(numValue.toString(), 10);
      setDuration(parsedNumValue);
      if (props?.assemblyComponent?.uid) {
        props.onUpdateRotationDuration(
          props.assemblyComponent.uid,
          parsedNumValue * 1000,
          type,
        );
      }
    }
  };

  const handleAddBackup = () => {
    setOpenComponentListModal(true);
  };

  const onSelectedComponent = useCallback(
    async (component: IComponent) => {
      let updatedDuration = duration;
      if (
        component?.component_type?.name === ComponentTypes.PROMOTIONAL_SPACE
      ) {
        const componentBackgroundType = getFileExtensionType(
          component?.config?.background,
        );
        if (componentBackgroundType === FileTypes.VIDEO) {
          updatedDuration =
            (await getBackgroundVideoDuration(
              component?.config?.background,
              duration ||
                props.assemblyComponent?.config?.duration ||
                DEFAULT_ASSEMBLY_COMPONENT_CONFIG.duration,
            )) || 3;
        }
      }

      props.onSelectBackupComponent(
        component,
        props.assemblyComponent.rotation_order,
        (updatedDuration || 0) * 1000,
      );
      setOpenComponentListModal(false);
    },
    [duration, props],
  );

  const debouncedSelectComponent = useMemo(
    () =>
      debounce((component: IComponent) => {
        onSelectedComponent(component);
      }, 500),
    [onSelectedComponent],
  );

  const onCloseComponentModal = () => {
    setOpenComponentListModal(false);
  };

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    const newItems = sortBy(backupAssemblyComponent, 'backup_order', 'asc');

    // Swap the components
    [newItems[oldIndex], newItems[newIndex]] = [
      newItems[newIndex],
      newItems[oldIndex],
    ];

    // Update backup_order values based on the new order
    newItems.forEach((item, index) => {
      // Update the backup_order value
      item.backup_order = index + 1;
    });

    props.handleUpdateBackupAssemblyComponents &&
      props.handleUpdateBackupAssemblyComponents(newItems);
  };

  const filteredComponentIds: number[] = concat(
    compact(
      props.backupAssemblyComponent?.map((item) => item.component?.id ?? 0),
    ),
    props.assemblyComponent.component?.id ?? 0,
  );

  const sectionPreviewData = useMemo(() => {
    return getSectionPreviewData(
      props.assemblyComponent.component?.component_type?.name as ComponentTypes,
      props.isCodConfirmationOpen
        ? DynamicSectionEvents.COD_CONFIRMATION_OPEN
        : DynamicSectionEvents.COD_ORDER_OPEN,
    );
  }, [
    props.assemblyComponent.component?.component_type?.name,
    props.isCodConfirmationOpen,
  ]);

  const isVariationAssembly =
    props.assemblyId !== undefined &&
    props.assemblyId !== props.assemblyComponent.assembly_id;

  const componentType = getFileExtensionType(
    props.assemblyComponent.component?.config.background,
  );

  return (
    <GridContainer container spacing={2}>
      <Grid item xs={12} width={361}>
        <CustomTooltip
          width={500}
          placement="right"
          title={props.assemblyComponent.component?.name}
          overflowLineNumber={2}>
          <Title>{props.assemblyComponent.component?.name}</Title>
        </CustomTooltip>
      </Grid>
      <PreviewGrid
        item
        $isConfirmation={(
          props.assemblyComponent.component?.component_type?.name ===
            ComponentTypes.COD && !!props.isCodConfirmationOpen
        ).toString()}>
        {props.assemblyComponent.component?.config && (
          <ScaleContainer
            containerStyle={{
              height: 160,
              maxWidth: 300,
            }}>
            <FullGcfComponentSection
              assemblyComponents={[
                {
                  component: {
                    type: props.assemblyComponent.component?.component_type
                      ?.name,
                    config: props.assemblyComponent.component?.config,
                    name: props.assemblyComponent.component?.name,
                    width: props.assemblyComponent.component?.width,
                    height: props.assemblyComponent.component?.height,
                    theme: props.assemblyComponent.component?.theme?.name,
                  },
                },
              ]}
              height={props.assemblyComponent.component?.height || 0}
              width={props.assemblyComponent.component?.width || 0}
              containerStyle={{
                borderWidth: '3px',
                borderStyle: 'solid',
                borderColor: 'brand.darkGrey',
              }}
              previewData={sectionPreviewData}
              productFilters={props.productFilters}
              productVariationFilters={props.productVariationFilters}
              skipProductsApiOnEmptyFilters={
                props.skipProductsApiOnEmptyFilters
              }
            />
          </ScaleContainer>
        )}
      </PreviewGrid>

      {!props.editable && (
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<LoopIcon />}
              onClick={props.onReplacedComponent}>
              {props.replaceCurrentComponentText ?? lang.replace}
            </Button>
          </Grid>
        </Grid>
      )}

      {props.availableRotations > 1 && (
        <Grid item xs={12}>
          <InputTextLabel>{lang.duration}</InputTextLabel>
          <InputTextField
            type="number"
            autoComplete="off"
            variant="outlined"
            value={duration?.toFixed(1)?.replace(/[.,]0$/, '') ?? 3} // default value is 3 seconds
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleChangeDuration(event.target.value, DurationUpdateTypes.MAIN)
            }
            disabled={
              props.editable ||
              isVariationAssembly ||
              componentType === FileTypes.VIDEO
            }
            size="small"
            inputProps={{
              min: 1,
              autoComplete: 'off',
              form: {
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
      )}

      {props.showSoftSellInfo &&
        (props.assemblyComponent.component?.start_date ||
          props.assemblyComponent.component?.end_date) && (
          <Grid item xs={12}>
            <Alert severity="info">
              {lang.softSellInfo.dateDescription(
                props?.assemblyComponent?.component?.start_date,
                props?.assemblyComponent?.component?.end_date,
              )}
            </Alert>
          </Grid>
        )}

      {props.assemblyComponent.component?.component_type?.name ===
        ComponentTypes.PROMOTIONAL_SPACE && (
        <Grid container item xs={12}>
          {!isEmpty(backupAssemblyComponent) && (
            <>
              <Grid item xs={9}>
                <Typography>{lang.backup}</Typography>
              </Grid>
              <Alert severity="info">
                {lang.defaultDurationBackupAssembly}
              </Alert>
              <Grid item xs={12}>
                <BackupComponentList
                  components={backupAssemblyComponent}
                  sortable={!isVariationAssembly}
                  onSortEnd={onSortEnd}
                  editable={props.editable}
                  showSoftSellInfo={props.showSoftSellInfo}
                  disabled={isVariationAssembly}
                  onDeletedBackupAssemblyComponent={
                    props.onDeletedBackupAssemblyComponent
                  }
                  handleBackupComponentSelection={
                    handleBackupComponentSelection
                  }
                  rotations={props.availableRotations}
                />
              </Grid>
            </>
          )}

          {!props.editable && (
            <CustomTooltip
              title={
                props.backupAssemblyComponent &&
                props.backupAssemblyComponent?.filter(
                  (item) =>
                    item.rotation_order ===
                    props.assemblyComponent.rotation_order,
                )?.length >= MAX_ALLOWED_COMPONENTS
                  ? lang.addBackupTooltip
                  : ''
              }
              placement="right"
              arrow
              isSecondaryTooltip={true}
              fontSize={14}
              skipValidateOverflow={true}>
              {/* Do not remove <div>, otherwise tooltip won't work */}
              <div>
                <Grid item xs={9}>
                  <AddBackupButton
                    disabled={
                      props.backupAssemblyComponent &&
                      props.backupAssemblyComponent.filter(
                        (item) =>
                          item.rotation_order ===
                          props.assemblyComponent.rotation_order,
                      ).length >= MAX_ALLOWED_COMPONENTS
                    }
                    onClick={handleAddBackup}>
                    {lang.addBackup}
                  </AddBackupButton>
                </Grid>
              </div>
            </CustomTooltip>
          )}
        </Grid>
      )}

      {!props.editable && (
        <Grid item xs={9}>
          <Button
            onClick={removeComponentModal.onOpenModal}
            variant="text"
            color="error">
            {lang.removeComponent}
          </Button>
        </Grid>
      )}

      {props.availableRotations > 1 && !props.editable && (
        <>
          <Grid item xs={1}>
            {props.componentIndex > 0 ? (
              <IconButton
                size="small"
                onClick={() =>
                  props.onReorderRotation(props.assemblyComponent, 'up')
                }>
                <ArrowUpwardIcon fontSize="inherit" />
              </IconButton>
            ) : null}
          </Grid>
          <Grid item xs={1}>
            {props.componentIndex + 1 < props.availableRotations && (
              <IconButton
                size="small"
                onClick={() =>
                  props.onReorderRotation(props.assemblyComponent, 'down')
                }>
                <ArrowDownwardIcon fontSize="inherit" />
              </IconButton>
            )}
          </Grid>
        </>
      )}

      {!props.editable && (
        <Grid item xs={12}>
          <Divider />
        </Grid>
      )}

      {componentPreviewModal.open && (
        <PreviewModal
          open={componentPreviewModal.open}
          onClose={componentPreviewModal.onCloseModal}
          type="component"
          content={
            {
              ...selectedPreviewComponent?.component,
              ...{ section: props.assemblyComponent.component },
            } as IComponent
          }
          hideButtons
        />
      )}

      <SimpleDialog
        actionColor="error"
        open={removeComponentModal.open}
        cancelText={lang.cancel}
        confirmText={lang.deleteText}
        title={props.removeComponentTitleText ?? lang.removeComponentTitle}
        onClose={removeComponentModal.onCloseModal}
        onClickCancel={removeComponentModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleRemoveComponent}
        adjustTitlePadding={true}
      />

      {openComponentListModal && (
        <ComponentListModal
          open={openComponentListModal}
          filters={[
            `component_type_id=${props.assemblyComponent.component?.component_type_id}`,
            `end_date.from=${currentDate}`,
            `id.notlist=${filteredComponentIds.join('|')}`,
            `height=${props.assemblyComponent.component?.height}`,
            `width=${props.assemblyComponent.component?.width}`,
            `layout_type_id=${props.assemblyComponent.component?.layout_type_id}`,
          ]}
          onClose={onCloseComponentModal}
          onSelectedComponent={debouncedSelectComponent}
        />
      )}
    </GridContainer>
  );
};
